/**
 * The scan control, and the one piece of logic on this page that matters.
 *
 * It appeared twice on the old homepage as two copies of the same form, two
 * copies of the same state, and two copies of the same error handling. It is
 * one component now, used at the top and at the bottom, so the two cannot
 * drift apart the way they already had: the hero said "Results in 30
 * seconds." and the closing band said something else.
 *
 * `startPublicSiteScan` is untouched. This is the page's whole conversion
 * path and restyling is not a reason to go near it.
 *
 * The box is drawn only while sign-up first is off. If the switch is turned
 * on while a tab is open, the server refuses the scan and
 * `startPublicSiteScan` takes the visitor to sign-up without throwing, so
 * nothing here shows an error: the box stays busy until the page changes.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AddressBar, type AddressState } from '@/design-system/primitives'
import HubSpotIcon from '@/components/icons/HubSpotIcon'
import { PublicScanPrepOverlay } from '@/components/onboarding/PublicScanPrepScreen'
import { startPublicSiteScan } from '@/utils/onboardingScan'

export default function HomeScan({ buttonLabel }: { buttonLabel?: string }) {
  const navigate = useNavigate()
  const [url, setUrl] = useState('')
  const [state, setState] = useState<AddressState>('idle')
  const [error, setError] = useState<string>()

  async function submit(value: string) {
    setState('loading')
    setError(undefined)
    try {
      await startPublicSiteScan(value, navigate)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not start the scan. Please try again.')
      setState('error')
    }
  }

  return (
    <>
      {state === 'loading' && <PublicScanPrepOverlay />}
      <AddressBar
        size="lg"
        value={url}
        onChange={(v) => {
          setUrl(v)
          if (state === 'error') setState('idle')
        }}
        onSubmit={submit}
        state={state}
        error={error}
        buttonLabel={buttonLabel ?? 'Run my free scan'}
        /* HubSpot's mark, in its own colour, where the globe would be: the
           scan is for HubSpot sites and the field says so before a word is
           read. Decorative; the note under the box carries the words. */
        icon={<HubSpotIcon className="ds-address__mark" aria-hidden="true" />}
      />
    </>
  )
}
